import React, { useState, useMemo, useCallback } from "react";
import { Tab } from "@headlessui/react";
import { RfTab } from "../../RfTab/RfTab";
import Button from "../../Button";
import InlineAlert from "../../InlineAlert";
import { CopyToClipboardButton } from "../../CopyToClipboardButton";
import { CodeTab } from "./types";
import { codeTabs } from "./constants";
import { buildSnippet, maskUrlKey } from "./utils";

export function CodeSnippetView({
    downloadUrl,
    format,
    workspaceUrl,
    datasetName,
    apiKey,
    notebookUrl,
    onClose
}: {
    downloadUrl: string;
    format: string;
    workspaceUrl?: string;
    datasetName?: string;
    apiKey?: string | null;
    notebookUrl?: string;
    onClose: () => void;
}) {
    const [activeTab, setActiveTab] = useState<CodeTab>(() => {
        try {
            const saved = localStorage.getItem("exportCodeTab");
            if (saved && codeTabs.some((t) => t.key === saved)) return saved as CodeTab;
        } catch {
            /* ignore */
        }
        return "jupyter";
    });
    const [keyVisible, setKeyVisible] = useState(false);
    const fullUrl = `${window.location.origin}${downloadUrl}`;
    const maskedFullUrl = maskUrlKey(fullUrl);

    const jupyterSnippet = useMemo(
        () => buildSnippet(format, workspaceUrl, datasetName, apiKey),
        [format, workspaceUrl, datasetName, apiKey]
    );
    const maskedJupyterSnippet = useMemo(
        () => buildSnippet(format, workspaceUrl, datasetName, apiKey, true),
        [format, workspaceUrl, datasetName, apiKey]
    );

    const copySnippets: Record<CodeTab, string> = useMemo(
        () => ({
            jupyter: jupyterSnippet,
            terminal: `curl -L "${fullUrl}" > roboflow.zip; unzip roboflow.zip; rm roboflow.zip`,
            raw: fullUrl
        }),
        [jupyterSnippet, fullUrl]
    );

    const displaySnippets: Record<CodeTab, string> = useMemo(
        () => ({
            jupyter: keyVisible ? jupyterSnippet : maskedJupyterSnippet,
            terminal: keyVisible
                ? `curl -L "${fullUrl}" > roboflow.zip; unzip roboflow.zip; rm roboflow.zip`
                : `curl -L "${maskedFullUrl}" > roboflow.zip; unzip roboflow.zip; rm roboflow.zip`,
            raw: keyVisible ? fullUrl : maskedFullUrl
        }),
        [jupyterSnippet, maskedJupyterSnippet, keyVisible, fullUrl, maskedFullUrl]
    );

    const activeIndex = codeTabs.findIndex((t) => t.key === activeTab);

    const handleTabChange = useCallback((index: number) => {
        const tab = codeTabs[index].key;
        setActiveTab(tab);
        try {
            localStorage.setItem("exportCodeTab", tab);
        } catch {
            /* ignore */
        }
    }, []);

    const handleCopyAndOpenNotebook = useCallback(() => {
        navigator.clipboard
            .writeText(jupyterSnippet)
            .catch(() => {})
            .finally(() => {
                window.open(notebookUrl || "https://colab.research.google.com/", "_blank");
                onClose();
            });
    }, [jupyterSnippet, notebookUrl, onClose]);

    return (
        <div className="flex flex-col gap-3">
            <Tab.Group selectedIndex={activeIndex} onChange={handleTabChange}>
                <Tab.List className="flex gap-1 border-b border-gray-200">
                    {codeTabs.map((tab) => (
                        <RfTab key={tab.key}>
                            <i className={tab.icon} />
                            {tab.label}
                        </RfTab>
                    ))}
                </Tab.List>
            </Tab.Group>
            <div className="relative">
                <pre className="overflow-x-auto rounded-md bg-gray-50 p-3 pr-10 text-xs text-gray-800">
                    {displaySnippets[activeTab]}
                </pre>
                <div className="absolute right-2 top-2 flex items-center gap-1">
                    {apiKey && (
                        <Button
                            type="ghost"
                            size="small"
                            shape="circle"
                            onClick={() => setKeyVisible((v) => !v)}
                            title={keyVisible ? "Hide API key" : "Show API key"}
                        >
                            <i className={`fal ${keyVisible ? "fa-eye-slash" : "fa-eye"} !m-0`} />
                        </Button>
                    )}
                    <CopyToClipboardButton text={copySnippets[activeTab]} />
                </div>
            </div>
            {activeTab === "jupyter" && (
                <Button
                    type="secondary"
                    size="small"
                    className="self-start"
                    onClick={handleCopyAndOpenNotebook}
                >
                    <i className="far fa-book-open" />
                    Copy &amp; Open Notebook
                </Button>
            )}
            <InlineAlert level="warning" type="secondary" dense>
                This snippet contains a private download link. Do not share it outside your team.
            </InlineAlert>
        </div>
    );
}
